import { SkyButton, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { Link, useSearch } from "wouter";
import {
  NOTIFY_SUBJECTS,
  RULES_STALE_MESSAGE,
  type NotificationRulesView,
  type NotifySubject,
} from "@shared/types/notifications";
import { LoadingRows, PermissionNotice } from "@/components/access/ScreenStates";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { EmptyState, ErrorState } from "@/components/platform/ScreenState";
import { DeliveriesTable } from "@/components/settings/DeliveriesTable";
import { QuietRunsSection } from "@/components/settings/QuietRunsSection";
import { NotificationRulesForm } from "@/components/settings/NotificationRulesForm";
import { AllowedDomainsCard } from "@/components/settings/AllowedDomainsCard";
import { ScopedRulesCard } from "@/components/settings/ScopedRulesCard";
import { SettingsTabs } from "@/components/settings/SettingsTabs";
import { TestNotificationButton } from "@/components/settings/TestNotificationButton";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { usePermissions } from "@/hooks/use-permissions";
import { useMonitorEnabled } from "@/hooks/use-monitor";
import {
  useNotificationDeliveries,
  useNotificationRules,
  useSaveNotificationRules,
} from "@/hooks/use-notifications";
import { ApiClientError } from "@/lib/api";
import { InlineErrorBar } from "@/lib/feedback/InlineErrorBar";
import { describeFailure, reportFailureShown, type DescribedFailure } from "@/lib/feedback/failure";
import { useDismissNoticeWhen, useNotice } from "@/lib/feedback/NoticeProvider";
import { useLeaveGuard } from "@/lib/leaveGuard";
import { RecipientPolicyProvider } from "@/lib/notifications/RecipientPolicyContext";
import {
  addRuleDraft,
  bodyFromDraft,
  cellKey,
  copiedSentence,
  copyToEverySubject,
  shownSubjects,
  draftDiffers,
  draftFromView,
  emailsProblem,
  heldAddresses,
  fieldErrorsFromDetails,
  nobodyNotified,
  offRulesSentence,
  repeatWindowProblem,
  type RuleDraft,
  type RulesDraft,
} from "@/lib/notifications/rulesDraft";
import { ROUTES, absolutePath } from "@/lib/routes";

/**
 * Settings > Notifications (contract-notif R4, R5, R6, R10, R13, R14; D1).
 *
 * Who hears about this instance's runs: one rule per (What, When) cell (`RULE_CELLS`), each with
 * its own addresses and Teams switch, plus the repeat window. **Nothing is implicit**: with no
 * rule row nothing sends to the instance's people, and the tab says "No notification rule yet"
 * with one action, "Add a rule", which opens the form pre-filled and stores nothing until Save.
 * The L1 team (Skypoint support) is not a rule's choice: it hears every failure whenever the
 * deployment has L1 recipients (round 3), so nothing here names it.
 *
 * Nothing is kept until Save changes, so leaving the tab with an edit asks first
 * (`useLeaveGuard`: "Save changes?" in the app, the browser's prompt on closing the tab).
 *
 * It is a tab of Settings, rendered by the Settings page's nested route, so it inherits that
 * route's guard; a role that reaches it without `instance.settings.write` sees the rules as text
 * with no Save, no copy and no test (the page's read-only branch).
 *
 * Feedback follows ux-s10 §3: the save confirmation is the page notice under the title; a save
 * error is an inline bar directly above the save bar; the copy and test results stay in their own
 * status lines. The Teams webhook is not edited here (round-1 ruling 14): the line names where it
 * is and links there.
 */

const SAVED_NOTICE_KEY = "notification-rules-saved";

/** The inline bar's content for a failure whose wording the contract fixes (§4.5). */
function fixedFailure(cause: unknown, message: string): DescribedFailure {
  return { ...describeFailure(cause), message, printedReference: null, retryable: false };
}

function TeamsLine({ view }: { view: NotificationRulesView }) {
  const settingsLink = (
    <Link href={absolutePath(ROUTES.settings)} className="font-medium underline underline-offset-2">
      General tab
    </Link>
  );
  return (
    <SkyText as="p" size={300} block testId="notifications-teams-line">
      {/* Connected or not, never the vault and secret names (lead addendum 2, SEC-17). */}
      {view.teamsConfigured ? (
        <>Teams channel: connected. Change it on the {settingsLink}.</>
      ) : (
        <>Teams channel: not connected. Add a webhook on the {settingsLink}.</>
      )}
    </SkyText>
  );
}

/** Where focus goes after an action that unmounts the control that was pressed (R14). */
type FocusTarget = "first-rule" | "add-rule";

const FOCUS_SELECTORS: Record<FocusTarget, string> = {
  "first-rule": '[data-testid="rule-enabled-import-failure"] [role="switch"]',
  "add-rule": '[data-testid="notifications-add-rule"]',
};

/** Nobody is notified yet, and email is not set up for this deployment. */
function RulesNotices({ view }: { view: NotificationRulesView }) {
  return (
    <>
      {nobodyNotified(view) ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="notifications-nobody"
          message="No one is notified about this instance's runs yet. Add an address or turn on the Teams channel in a rule that is on."
        />
      ) : null}
      {!view.emailConfigured ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="notifications-email-off"
          message={`Email is not set up for this deployment. Addresses are saved, and nothing is emailed until a platform admin sets it up.${
            view.teamsConfigured ? " Teams posts still go out." : ""
          }`}
        />
      ) : null}
    </>
  );
}

/** No rule yet, with "Add a rule" for a writer. */
function NoRuleYet({ onAdd }: { onAdd: (() => void) | undefined }) {
  return (
    <EmptyState
      title="No notification rule yet"
      message="Choose who hears about failed, warned or successful runs of this instance, for imports, transformations and schedules."
      testId="notifications-empty"
      action={
        onAdd ? (
          <SkyButton appearance="primary" testId="notifications-add-rule" onClick={onAdd}>
            Add a rule
          </SkyButton>
        ) : undefined
      }
    />
  );
}

/** Save and Discard, the refused save with Reload when it was stale, and when rules apply. */
function RulesSaveControls({
  dirty,
  adding,
  saving,
  saveError,
  onSave,
  onDismissError,
  onReload,
  onDiscard,
}: {
  dirty: boolean;
  adding: boolean;
  saving: boolean;
  saveError: { failure: DescribedFailure; stale: boolean } | null;
  onSave: () => void;
  onDismissError: () => void;
  onReload: () => void;
  onDiscard: () => void;
}) {
  return (
    <>
      <TopbarActionsPortal>
        <SkyButton
          appearance="primary"
          testId="save-notifications-topbar"
          disabled={!dirty}
          loading={saving}
          onClick={onSave}
        >
          Save changes
        </SkyButton>
      </TopbarActionsPortal>

      {saveError ? (
        <div className="flex flex-col items-start gap-2">
          <InlineErrorBar
            error={saveError.failure}
            testId="notifications-save-error"
            onDismiss={onDismissError}
          />
          {saveError.stale ? (
            <SkyButton
              appearance="secondary"
              size="small"
              testId="reload-notifications"
              onClick={onReload}
            >
              Reload
            </SkyButton>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
        <SkyText as="p" size={200} color="muted">
          Rules apply to the next run that finishes. A run already finished is not sent again.
        </SkyText>
        <span className="ml-auto flex items-center gap-2">
          <SkyButton
            appearance="subtle"
            testId="discard-notifications"
            disabled={!dirty && !adding}
            onClick={onDiscard}
          >
            Discard
          </SkyButton>
          <SkyButton
            appearance="primary"
            testId="save-notifications"
            disabled={!dirty}
            loading={saving}
            onClick={onSave}
          >
            Save changes
          </SkyButton>
        </span>
      </div>
    </>
  );
}

/**
 * The draft follows the view during render (as `settings.tsx` does): seeded from the first view
 * (or another instance's), the pre-filled add form once a settled read allows it, and a newer
 * version shown while nothing was edited.
 */
function reseedDuringRender({
  view,
  draft,
  seed,
  adding,
  addPending,
  fetching,
  wantsAdd,
  canWrite,
  subjects,
  set,
}: {
  view: NotificationRulesView | undefined;
  draft: RulesDraft | null;
  seed: NotificationRulesView | null;
  adding: boolean;
  addPending: boolean;
  fetching: boolean;
  /** `?add=1` (an "Add a rule" link) asks for the pre-filled form at once. */
  wantsAdd: boolean;
  canWrite: boolean;
  subjects: NotifySubject[];
  set: {
    setSeed: (seed: NotificationRulesView) => void;
    setAdding: (adding: boolean) => void;
    setDraft: (draft: RulesDraft) => void;
    setAddPending: (pending: boolean) => void;
  };
}): void {
  // Seeded during render from the first view the query yields (as `settings.tsx` does). A link
  // with `?add=1` (an "Add a rule" link) asks for the pre-filled form at once.
  if (view && (draft === null || seed === null || seed.instanceId !== view.instanceId)) {
    set.setSeed(view);
    set.setAdding(false);
    set.setDraft(draftFromView(view));
    set.setAddPending(!view.hasRules && wantsAdd && canWrite);
  } else if (view && addPending && !fetching) {
    // The add draft is built only from a read that is not in flight (review B1), so its Teams
    // switches follow the webhook as it is now. A read that found rules meanwhile shows them.
    set.setAddPending(false);
    set.setSeed(view);
    if (view.hasRules) {
      set.setAdding(false);
      set.setDraft(draftFromView(view));
    } else {
      set.setAdding(true);
      set.setDraft(addRuleDraft(view, subjects));
    }
  } else if (
    view &&
    draft &&
    seed &&
    view.version !== seed.version &&
    !adding &&
    !draftDiffers(draft, seed)
  ) {
    // A newer version arrived while nothing was edited: show it (nothing of the reader's is lost).
    set.setSeed(view);
    set.setDraft(draftFromView(view));
  }
}

/**
 * One form per instance (review r1 B1). Switching instance in the top bar keeps this route
 * mounted, so the tab is keyed by the selected instance: a switch throws away the draft, the
 * copy line and a test in flight, and the new instance's form is seeded from its own view. A
 * draft read from one instance can never be saved into another.
 */
/** The rules screen once the rules and their draft are read. */
function LoadedRules({
  view,
  draft,
  readOnly,
  canWrite,
  showForm,
  form,
  onEdit,
  onAdd,
  saveControls,
  deliveries,
  instanceName,
}: {
  view: NotificationRulesView;
  draft: RulesDraft;
  readOnly: boolean;
  canWrite: boolean;
  showForm: boolean;
  form: Pick<
    ComponentProps<typeof NotificationRulesForm>,
    "fieldErrors" | "copyStatus" | "revealErrors" | "initialOpen" | "onRuleChange" | "onCopy"
  >;
  onEdit: (draft: RulesDraft) => void;
  onAdd: () => void;
  saveControls: ComponentProps<typeof RulesSaveControls>;
  deliveries: ReturnType<typeof useNotificationDeliveries>;
  instanceName: string;
}) {
  return (
    <RecipientPolicyProvider policy={view.recipientPolicy} held={heldAddresses(view)}>
      <AllowedDomainsCard policy={view.recipientPolicy} />

      <RulesNotices view={view} />

      <TeamsLine view={view} />

      {showForm ? (
        <NotificationRulesForm
          draft={draft}
          readOnly={readOnly}
          hasWebhook={view.teamsConfigured}
          emailsMasked={view.emailsMasked}
          {...form}
          onRepeatWindowChange={(value) => onEdit({ ...draft, repeatWindow: value })}
        />
      ) : (
        <NoRuleYet onAdd={readOnly ? undefined : onAdd} />
      )}

      {canWrite && showForm ? <RulesSaveControls {...saveControls} /> : null}

      {canWrite && view.hasRules ? <TestNotificationButton /> : null}

      {/* Alert routing (2026-09-28): rules for chosen connections or schedules. Additive:
          they add recipients and never stop the rules above. */}
      <ScopedRulesCard canWrite={canWrite} hasRules={view.hasRules} />

      <DeliveriesTable
        rows={deliveries.data}
        isLoading={deliveries.isPending}
        error={deliveries.isError ? deliveries.error : null}
        instanceName={instanceName}
        hasRules={view.hasRules}
        onRetry={() => void deliveries.refetch()}
      />
      {/* Round 4 item 3: what was NOT sent, and why, one click under what was. */}
      <QuietRunsSection instanceName={instanceName} />
    </RecipientPolicyProvider>
  );
}

export default function AdminNotificationsPage() {
  const { selectedInstance } = useTenantInstance();
  return <NotificationsTab key={selectedInstance?.id ?? "none"} />;
}

function NotificationsTab() {
  const { can, isResolved } = usePermissions();
  const { selectedInstance } = useTenantInstance();
  const notice = useNotice();
  const search = useSearch();
  const canWrite = can("instance.settings.write");
  // `?open=data_health`: skyMonitor's "Alerts for these checks" link opens its section.
  const openParam = new URLSearchParams(search).get("open");
  const openRequested = NOTIFY_SUBJECTS.find((subject) => subject === openParam);
  const ready = isResolved && Boolean(selectedInstance);

  const rules = useNotificationRules(ready);
  const deliveries = useNotificationDeliveries(ready);
  const save = useSaveNotificationRules();

  const [draft, setDraft] = useState<RulesDraft | null>(null);
  const subjects = shownSubjects(useMonitorEnabled());
  // The view the draft was seeded from (review r1 B1, S1): Save writes to ITS instance and sends
  // ITS version, so a background read that moved the version under an edited draft ends in the
  // stale answer, never in a silent overwrite of another admin's change.
  const [seed, setSeed] = useState<NotificationRulesView | null>(null);
  const focusTarget = useRef<FocusTarget | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [adding, setAdding] = useState(false);
  // "Add a rule" was asked for (the button, or `?add=1`) and waits for a settled read (review
  // B1): the draft's Teams pre-fill comes from the view's webhook, and a cached view can predate
  // a webhook saved on the General tab, whose save invalidates this query.
  const [addPending, setAddPending] = useState(false);
  const [copyStatus, setCopyStatus] = useState<{ cell: string; text: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Bumped on each refused Save: the form opens every section with a rule to fix (review S2).
  const [revealErrors, setRevealErrors] = useState(0);
  const [saveError, setSaveError] = useState<{ failure: DescribedFailure; stale: boolean } | null>(
    null,
  );

  const view = rules.data;

  reseedDuringRender({
    view,
    draft,
    seed,
    adding,
    addPending,
    fetching: rules.isFetching,
    wantsAdd: new URLSearchParams(search).get("add") === "1",
    canWrite,
    subjects,
    set: { setSeed, setAdding, setDraft, setAddPending },
  });

  const dirty = useMemo(
    () => Boolean(canWrite && seed && draft && draftDiffers(draft, seed)),
    [canWrite, seed, draft],
  );
  useDismissNoticeWhen(SAVED_NOTICE_KEY, dirty);
  // Round 3 (owner, 2026-09-30): nothing is kept until Save changes, so leaving with an edit asks
  // "Save changes?" (in-app) or gets the browser's prompt (closing the tab).
  const leaveGuard = useLeaveGuard({ when: dirty, onSave: (proceed) => handleSave(proceed) });

  // R14: "Add a rule" and "Discard" can unmount the button that was pressed; focus goes to the
  // first control of what replaced it instead of falling to the page body. The handler sets the
  // state that shows the new control and names the target; the commit after it moves focus.
  useEffect(() => {
    if (!focusTarget.current) return;
    const target = pageRef.current?.querySelector<HTMLElement>(
      FOCUS_SELECTORS[focusTarget.current],
    );
    if (!target) return;
    focusTarget.current = null;
    const control =
      target.matches("button, input, [role='switch']") || target.tabIndex >= 0
        ? target
        : (target.querySelector<HTMLElement>("button, input") ?? target);
    control.focus();
  });

  if (!isResolved) return <LoadingRows rows={4} columns={2} />;
  if (!canWrite && !can("instance.read")) {
    return <PermissionNotice permission="instance.settings.write" />;
  }

  const readOnly = !canWrite;
  const showForm = Boolean(view && (view.hasRules || adding));

  /** Every edit clears the last copy's line and any stale field message: it is a new action. */
  function edit(next: RulesDraft) {
    setDraft(next);
    setCopyStatus(null);
  }

  function changeRule(next: RuleDraft) {
    if (!draft) return;
    const key = cellKey(next.subject, next.outcome);
    setFieldErrors((current) => {
      if (!(key in current)) return current;
      const rest = { ...current };
      delete rest[key];
      return rest;
    });
    edit({
      ...draft,
      rules: draft.rules.map((rule) =>
        rule.subject === next.subject && rule.outcome === next.outcome ? next : rule,
      ),
    });
  }

  function copyRule(rule: RuleDraft) {
    if (!draft || !view) return;
    setDraft(copyToEverySubject(draft, rule, subjects));
    setCopyStatus({
      cell: cellKey(rule.subject, rule.outcome),
      text: copiedSentence(rule.subject, subjects),
    });
  }

  function discard() {
    if (!view) return;
    // Back to the latest stored version (it is what the reader discards to).
    setSeed(view);
    setDraft(draftFromView(view));
    focusTarget.current = view.hasRules ? "first-rule" : "add-rule";
    setAdding(false);
    setAddPending(false);
    setCopyStatus(null);
    setFieldErrors({});
    setSaveError(null);
  }

  async function reload() {
    setSaveError(null);
    setFieldErrors({});
    setCopyStatus(null);
    const result = await rules.refetch();
    if (result.data) {
      setSeed(result.data);
      setDraft(draftFromView(result.data));
      setAdding(false);
      setAddPending(false);
      // Reload unmounts itself with the error bar (R14, review S1): focus goes to the first
      // control of what the reload shows instead of falling to the page body.
      focusTarget.current = result.data.hasRules ? "first-rule" : "add-rule";
    }
  }

  /** `onSaved` runs after a save that succeeded (the leave dialog's Save passes the navigation). */
  function handleSave(onSaved?: () => void) {
    if (!draft || !seed) return;
    // Review r2 S1, #449: the save goes to the instance the draft was read from (`lib/api.ts`
    // keeps a caller's `x-instance-id`), so this check is defence in depth: a draft read from one
    // instance is never saved while another is selected. The remount key and the render-time
    // reseed above make it unreachable today.
    if (seed.instanceId !== selectedInstance?.id) {
      setSeed(null);
      setDraft(null);
      setAdding(false);
      setCopyStatus(null);
      setFieldErrors({});
      setSaveError({
        failure: {
          message: `Nothing was saved. These rules were read from another instance, not ${selectedInstance?.name ?? "the selected one"}. They have been read again.`,
          reference: null,
          printedReference: null,
          retryable: false,
        },
        stale: false,
      });
      void rules.refetch();
      return;
    }
    // Every rule is sent, so every rule is checked, the ones that are off included (review r1 S3);
    // an off rule's box is hidden, so the bar names it.
    // #1111: by the policy and the stored addresses on screen now, as the boxes mark them (the
    // domains card's save refreshes the view, not the seed, while a draft is open).
    const current = view ?? seed;
    const held = heldAddresses(current);
    const badCells = draft.rules
      .filter(
        (rule) =>
          emailsProblem(
            rule.emailsText,
            current.recipientPolicy,
            held.get(cellKey(rule.subject, rule.outcome)) ?? [],
          ) !== null,
      )
      .map((rule) => cellKey(rule.subject, rule.outcome));
    const invalid = badCells.length > 0 || repeatWindowProblem(draft.repeatWindow) !== null;
    if (invalid) {
      setRevealErrors((count) => count + 1);
      const offLine = offRulesSentence(draft, badCells);
      setSaveError({
        failure: {
          message: [
            "Some fields need a fix before the rules can be saved. They are marked above.",
            offLine,
          ]
            .filter(Boolean)
            .join(" "),
          reference: null,
          printedReference: null,
          retryable: false,
        },
        stale: false,
      });
      return;
    }
    setSaveError(null);
    save.mutate(
      { instanceId: seed.instanceId, body: bodyFromDraft(draft, seed.version) },
      {
        onSuccess: (saved) => {
          setSeed(saved);
          setDraft(draftFromView(saved));
          setAdding(false);
          setCopyStatus(null);
          setFieldErrors({});
          notice.success("Notification rules saved. They apply to the next run that finishes.", {
            key: SAVED_NOTICE_KEY,
          });
          // The leave dialog's Save: the navigation it held back goes on once the rules are kept.
          onSaved?.();
        },
        onError: (error) => {
          reportFailureShown(error);
          if (error instanceof ApiClientError && error.code === "STALE_UPDATE") {
            setSaveError({
              failure: fixedFailure(error, RULES_STALE_MESSAGE),
              stale: true,
            });
            return;
          }
          const failure = describeFailure(error, "The notification rules could not be saved.");
          if (error instanceof ApiClientError && error.code === "VALIDATION_FAILED") {
            const mapped = fieldErrorsFromDetails(error.details);
            setFieldErrors(mapped);
            setRevealErrors((count) => count + 1);
            const offLine = offRulesSentence(draft, Object.keys(mapped));
            if (offLine) failure.message = `${failure.message} ${offLine}`;
          }
          setSaveError({ failure, stale: false });
        },
      },
    );
  }

  let rulesBody: ReactNode = null;
  if (rules.isPending) {
    rulesBody = (
      <div>
        <SkyText size={200} color="muted" block className="mb-2">
          Loading notification rules…
        </SkyText>
        <LoadingRows rows={4} columns={2} />
      </div>
    );
  } else if (rules.isError) {
    rulesBody = (
      <ErrorState
        title="Notification rules did not load"
        error={rules.error}
        onRetry={() => void rules.refetch()}
        testId="notifications-load-error"
      />
    );
  } else if (view && draft) {
    rulesBody = (
      <LoadedRules
        view={view}
        draft={draft}
        readOnly={readOnly}
        canWrite={canWrite}
        showForm={showForm}
        form={{
          fieldErrors,
          copyStatus,
          revealErrors,
          initialOpen: openRequested,
          onRuleChange: changeRule,
          onCopy: copyRule,
        }}
        onEdit={edit}
        onAdd={() => {
          // The draft is built at the next render from a settled read (review B1).
          setAddPending(true);
          setCopyStatus(null);
          focusTarget.current = "first-rule";
        }}
        saveControls={{
          dirty,
          adding,
          saving: save.isPending,
          saveError,
          onSave: () => handleSave(),
          onDismissError: () => setSaveError(null),
          onReload: () => void reload(),
          onDiscard: discard,
        }}
        deliveries={deliveries}
        instanceName={selectedInstance?.name ?? "this instance"}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="notifications-page" ref={pageRef}>
      {leaveGuard}
      <SettingsTabs active="notifications" />

      {readOnly ? <PermissionNotice permission="instance.settings.write" /> : null}

      {rulesBody}
    </div>
  );
}
